import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { useTranslation } from 'react-i18next';

import styles from './Dialog.module.css';
import { Button } from '../../primitives/Button/Button';
import { getOverlayRoot, isTopmostDialog, popDialog, pushDialog } from '../overlayRoot';

/**
 * Dialog — "Close case 15462647?", and the four seconds either side of it.
 *
 * Handoff, "Dialogs & toasts": max 420px, radius `lg`, `--shadow-lg`, a
 * `color-mix` backdrop, 22px padding, a 19px title, actions at the end of the
 * flow, focus trapped inside, Esc cancels, and for destructive actions the
 * confirm button is NOT the default focus.
 *
 * Five decisions worth reading before changing anything here. All five are
 * invisible to a sighted mouse user and decisive for everyone else.
 *
 * 1. **Modal means the page behind it is unavailable, in all three channels.**
 *    `aria-modal="true"` is a claim, not an enforcement — so the claim is
 *    backed: Tab is trapped (below), the pointer is blocked by the backdrop,
 *    and every other child of `<body>` is `aria-hidden` for as long as the
 *    dialog is open (overlayRoot.ts). A dialog that only traps Tab still lets a
 *    screen-reader user walk the page underneath with the virtual cursor.
 *
 * 2. **The trap listens on `document`, not on the dialog.** If focus is
 *    somewhere else entirely — the address bar, a background control a script
 *    focused — a keydown handler on the dialog's own subtree never fires, and
 *    the next Tab walks into the page behind. Listening at the document also
 *    keeps task 21's Escape contract intact: React 18 dispatches at its root
 *    container, which sits BELOW `document`, so a descendant that calls
 *    `stopPropagation()` on Escape (the Combobox closing its own listbox) stops
 *    this handler too. One Escape closes the listbox; the second closes the
 *    dialog.
 *
 * 3. **Focus comes back to whatever opened it.** Closing onto `<body>` leaves a
 *    keyboard user at the top of the document with no idea where they were.
 *
 * 4. **`destructive` arms nothing.** Not merely "the confirm button is not
 *    focused" — NOTHING is focused except the dialog itself, so a reflex Enter
 *    or Space cannot act, and the title and body are announced on open because
 *    focus landed on the container that names them.
 *
 * 5. **The backdrop does not dismiss.** The handoff grants exactly one
 *    dismissal gesture ("Esc | Close dialog or panel, discarding nothing"), and
 *    a dialog is the surface a half-typed justification sits on. A stray click
 *    on the dimmed page throwing that away is not a gesture anyone asked for.
 *    The close button and Escape are the two documented routes out.
 */

export interface DialogProps {
  open: boolean;
  /**
   * Handoff: "Title asks the question and names the record — 'Close case
   * 15462647?', never 'Are you sure?'". Guarded in development, scoped by
   * `kind` — see it.
   */
  title: string;
  /**
   * What this dialog IS, which is what decides whether its title should be
   * a question.
   *
   * `'confirm'` (the default) is the handoff's subject: a dialog whose
   * actions ANSWER its title — "Close case 15462647?" / Close | Cancel.
   * `'form'` is a dialog the user came to fill in, whose title names the
   * task — "New case". Asking "New case?" would be nonsense, and the
   * actions do not answer it.
   *
   * The development guard on the question mark therefore runs for
   * `'confirm'` only. The "names nothing" guard runs for both: "Are you
   * sure?" and a form headed "Confirm" are the same defect, an agent with
   * six tabs open unable to tell which record is in front of them.
   *
   * DEFAULTS TO `'confirm'` so the rule stays opt-out rather than opt-in —
   * a new confirmation dialog is guarded without its author knowing the
   * prop exists, which is the only way a lint-by-console rule survives.
   */
  kind?: 'confirm' | 'form';
  /** Escape, the close button, and whatever the caller's own actions call. */
  onClose: () => void;
  /**
   * The action this dialog confirms cannot be taken back. Changes the default
   * focus, and nothing else: the destructive *look* belongs to the caller's own
   * `<Button variant="destructive">`.
   */
  destructive?: boolean;
  /** One line of body copy. Becomes the dialog's accessible description. */
  description?: string;
  /** Anything more than one line: a form, a list of consequences. */
  children?: ReactNode;
  /** Handoff: "actions at the end of the flow." */
  actions?: ReactNode;
  /**
   * Where focus lands on open. The escape hatch a form dialog needs — without
   * it, a "Reassign case" dialog opens on its Cancel button rather than on the
   * field the agent came to type in.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * The handoff's 420px is the DEFAULT and stays the default: a dialog this
   * component does not explicitly opt out of does not move, ever. `'md'` and
   * `'lg'` exist for the caller who has more than a sentence and two buttons
   * to show — a form with several fields, a form whose fields want to sit in
   * pairs rather than stack one per row. Undefined, not a fourth named value
   * for 420: that keeps the regression guard a single "no prop at all" case
   * rather than a value someone can typo away from.
   */
  size?: 'md' | 'lg';
  className?: string;
}

/**
 * `details > summary` rather than `details`: the summary is the focusable part.
 *
 * Two filters are deliberately absent, and both were tried:
 *
 *  - **visibility.** jsdom has no layout, so a filter on it would behave
 *    differently in the two environments and make the trap untestable in the
 *    one that can run every test.
 *  - **`aria-hidden`.** This list decides only where the cycle WRAPS; between
 *    the two ends the browser does the walking, and the browser does not skip
 *    an `aria-hidden` control. Filtering here made the two disagree — the first
 *    Tab past the last "known" control landed on the very element the filter
 *    existed to skip. Making the trap drive every Tab instead would fix the
 *    disagreement and break a radio group (native Tab visits the CHECKED radio
 *    only, which task 21's SegmentedControl relies on), so the list matches the
 *    browser rather than second-guessing it.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'details > summary',
].join(', ');

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  return root === null ? [] : [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/** ASCII `?` or the Arabic question mark U+061F. */
const ASKS_A_QUESTION = /[?؟]\s*$/;

/** The handoff's own counter-example, and its nearest relatives. */
const NAMES_NOTHING = /^\s*(?:are you sure|confirm|please confirm|warning|attention)\b/i;

function guardTitle(title: string, kind: 'confirm' | 'form'): void {
  if (kind === 'confirm' && !ASKS_A_QUESTION.test(title)) {
    console.error(
      `Dialog: the title "${title}" asks a question in neither locale. The handoff makes a ` +
        'dialog title the question itself — "Close case 15462647?" — because the actions ' +
        'answer it.',
    );
    return;
  }
  if (NAMES_NOTHING.test(title)) {
    console.error(
      `Dialog: the title "${title}" names the record it acts on nowhere. "Are you sure?" is ` +
        'the handoff\'s own example of the title to never write: an agent with six tabs open ' +
        'cannot tell which case they are about to close.',
    );
  }
}

export const Dialog = forwardRef<HTMLDialogElement, DialogProps>(function Dialog(
  { open, ...rest },
  ref,
) {
  // Lazily, once: creating it in an effect would put the first paint of the
  // dialog one frame after the state that opened it.
  const [host] = useState(getOverlayRoot);

  if (!open) return null;
  return createPortal(<DialogPanel {...rest} ref={ref} />, host);
});

const DialogPanel = forwardRef<HTMLDialogElement, Omit<DialogProps, 'open'>>(function DialogPanel(
  {
    title,
    kind = 'confirm',
    onClose,
    destructive = false,
    description,
    children,
    actions,
    initialFocusRef,
    size,
    className,
  },
  ref,
) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDialogElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const id = useId();
  const titleId = `${id}title`;
  const descriptionId = `${id}description`;

  /**
   * The mount-only effects below read props that must NOT re-run them: focus
   * must be placed once, and `onClose` must be current when Escape is pressed
   * without re-binding the listener on every render. The initial value is
   * already the mount-time value, so the mount effect reads the right thing;
   * the effect underneath keeps it fresh for later.
   */
  const latest = useRef({ onClose, destructive, initialFocusRef });
  useEffect(() => {
    latest.current = { onClose, destructive, initialFocusRef };
  });

  /**
   * IN AN EFFECT, NOT IN THE RENDER BODY. Called inline it re-ran on every
   * render — a single case creation put roughly twenty copies of the same
   * message in the console, which is how a guard stops being read. Keyed on
   * the title and kind, it speaks once per distinct title.
   */
  useEffect(() => {
    if (import.meta.env.DEV) guardTitle(title, kind);
  }, [title, kind]);

  const attachPanel = useCallback(
    (node: HTMLDialogElement | null) => {
      panelRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== null) ref.current = node;
    },
    [ref],
  );

  useEffect(() => {
    const panel = panelRef.current;
    if (panel === null) return;

    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    pushDialog(panel);

    const { destructive: isDestructive, initialFocusRef: requested } = latest.current;
    const target =
      requested?.current ??
      (isDestructive ? panel : (focusableWithin(contentRef.current)[0] ?? closeRef.current ?? panel));
    target.focus();

    return () => {
      popDialog(panel);
      // `isConnected`: the invoker is routinely the row button of a row this
      // dialog just deleted.
      if (invoker !== null && invoker.isConnected) invoker.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const panel = panelRef.current;
      if (panel === null || !isTopmostDialog(panel)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        latest.current.onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(panel);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (first === undefined || last === undefined) {
        // Nothing to cycle between, so Tab must still not leave.
        event.preventDefault();
        panel.focus();
        return;
      }
      if (active === null || !panel.contains(active) || active === panel) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <>
      <div className={styles['backdrop']} data-testid="dialogBackdrop" aria-hidden="true" />

      <dialog
        open
        ref={attachPanel}
        className={[styles['dialog'], size === undefined ? null : styles[size], className]
          .filter(Boolean)
          .join(' ')}
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description === undefined ? {} : { 'aria-describedby': descriptionId })}
        tabIndex={-1}
      >
        <div className={styles['header']}>
          {/*
            A heading as well as the accessible name. The name comes from
            aria-labelledby either way; the heading is what lets a screen-reader
            user jump back to the question after reading the body. ListState's
            argument against a heading does not apply — a modal dialog is its own
            document context, not a fragment inside someone else's outline.
          */}
          <h2 id={titleId} className={styles['title']}>
            {title}
          </h2>
          <Button
            ref={closeRef}
            variant="icon"
            aria-label={t('a11y.closeDialog')}
            icon={<XIcon size={16} />}
            onClick={onClose}
          />
        </div>

        {/*
          `data-field-span="fill"` releases the content-length caps that
          Field.module.css puts on `width="code" | "compact" | "phone"`, for
          the fields inside THIS panel only. The handoff's rule — "a full-width
          input for a short value is a bug" — is a rule about a LIST SCREEN's
          filter bar, where a 90px code box sits beside five other controls in
          one row and its width is the only hint of how much to type. A dialog
          is a single column at most 420-720px wide, so the same rule leaves a
          130px box floating in 300px of empty panel, which reads as a broken
          layout rather than as a hint. The caps stay in force everywhere else;
          see the `[data-field-span='fill']` block in Field.module.css.
        */}
        <div ref={contentRef} className={styles['content']} data-field-span="fill">
          {description === undefined ? null : (
            <p id={descriptionId} className={styles['description']}>
              {description}
            </p>
          )}
          {children}
          {actions === undefined ? null : <div className={styles['actions']}>{actions}</div>}
        </div>
      </dialog>
    </>
  );
});
